import React, {useContext, useState} from "react";
import {useNavigate} from "react-router-dom";
import {CartContext} from "../../../Contexts/CartContext";
import {GET, POST} from "../../../Hooks/RequestV2";
import {SimpleModal, useSimpleModalState} from "../../../Components/SimpleModal";
import {useTranslation} from "react-i18next";
import {isCartQuantityLimitError} from "../../Cart/QuantityLimit/quantityLimit";
import {QuantityLimitError} from "../../Cart/QuantityLimit/QuantityLimitError";

function useTransferSavedCartToCart(
    setThrobber: React.Dispatch<any>,
    idItem: number,
    endpoint: string,
    onError: (quantityLimitExceeded: boolean) => void,
){
    const cartContext = useContext(CartContext);
    const navigate    = useNavigate();

    function saveCartInCurrentCart(path: string, type: string): void {
        setThrobber(true);
        POST(endpoint, {id: idItem, type: type})
            .then(async (response) => {
                if (!response.ok) {
                    const payload: unknown = await response.json().catch(() => null);
                    setThrobber(false);
                    onError(response.status === 422 && isCartQuantityLimitError(payload));
                    return;
                }

                const cartResponse = await GET('/user/cart');

                if (!cartResponse.ok) {
                    throw new Error(`Cart refresh failed with status ${cartResponse.status}.`);
                }

                const json = await cartResponse.json();
                setThrobber(false);
                cartContext!.setCart(json);
                navigate(path);
            })
            .catch(() => {
                setThrobber(false);
                onError(false);
            });
    }

    return saveCartInCurrentCart;
}

function SavedCartModal({setThrobber, idItem, endpoint}: {setThrobber:React.Dispatch<any>, idItem: number, endpoint: string}): React.ReactElement {
    const {visibility, setVisibility, toggleVisibleModal} = useSimpleModalState();
    const [requestError, setRequestError] = useState(false);
    const [quantityLimitError, setQuantityLimitError] = useState(false);
    const sendItems = useTransferSavedCartToCart(setThrobber, idItem, endpoint, (quantityLimitExceeded) => {
        setQuantityLimitError(quantityLimitExceeded);
        setRequestError(!quantityLimitExceeded);
    });
    const cartContext = useContext(CartContext);
    const {t} = useTranslation();
    const splitURL = endpoint.split('/');
    const navigate = useNavigate();
    const [path, setPath] = useState<string>('/cart?saved-cart-transfer=true');

    function enabledModal(targetPath: string): void {
        if (cartContext?.cart?.products.length !== 0){
            setPath(targetPath);
            setVisibility(true as any);
        } else {
            sendItems(targetPath, 'stay');
        }
    }

    function isActiveOrServiceItemsValidator(): void {
        setRequestError(false);
        setQuantityLimitError(false);

        POST('/user/validate/document', {id: idItem, type: splitURL[2]})
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`Document validation failed with status ${response.status}.`);
                }

                return response.json();
            })
            .then((json) => {
                if (json === 'only_service' || json === 'not_active'){
                    navigate('/cart?failed-cart-transfer=true')
                } else if (json === 'all_active') {
                    enabledModal('/cart?saved-cart-transfer=true');
                } else {
                    enabledModal('/cart?mixed-cart-transfer=true');
                }
            })
            .catch(() => setRequestError(true));

    }

    return (
        <>
            <button type={'button'} className="btn green" onClick={() => isActiveOrServiceItemsValidator()} >
                <i className="fas fa-cart-arrow-down" ></i> {t('user-page.saved-carts.add-to-cart')}
            </button>
            {requestError && (
                <div className="form-error">
                    <i className="fas fa-times-circle" />
                    <span>{t('user-page.saved-carts.validation-error')}</span>
                </div>
            )}
            {quantityLimitError && <QuantityLimitError />}
            <SimpleModal className={'add-to-cart-modal'} hidden={toggleVisibleModal} visibility={visibility} wait={true} >
                <p>{t('user-page.saved-carts.add-to-cart-modal-text')}:</p>

                <div className={'buttons'} >
                    <button type={'button'} className={'btn red'} onClick={() => setVisibility(false as any)} ><i className="fas fa-arrow-circle-left" /> {t('user-page.saved-carts.add-to-cart-modal-no')}</button>
                    <button type={'button'} className={'btn green'} onClick={() => { setVisibility(false as any); sendItems(path, 'remove'); }} ><i className="fas fa-times-circle" /> {t('user-page.saved-carts.add-to-cart-modal-remove')}</button>
                    <button type={'button'} className={'btn green'} onClick={() => { setVisibility(false as any); sendItems(path, 'stay'); }} ><i className="fas fa-cart-plus" /> {t('user-page.saved-carts.add-to-cart-modal-append')}</button>
                </div>
            </SimpleModal>
        </>
    );
}

export {SavedCartModal}
